import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { finalize, take } from 'rxjs/operators';

import { UserService } from '@core/api/user.service';
import { RedirectService } from '@core/helper/redirect.service';
import * as fromApp from '@store';
import { setCurrentUser } from '@store/auth/auth.actions';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { states } from '@shared/constants/states.contants';
import { User } from '@shared/models';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { FormatHelperService } from '@core/helper/format-helper.service';

@Component({
  selector: 'fc-verify-account',
  templateUrl: './verify-account.component.html',
  styleUrls: ['./verify-account.component.scss'],
  standalone: false
})
export class VerifyAccountComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  verifyUserForm: UntypedFormGroup;
  currentUser: User;

  stateList = states;
  dobInvalidMsg = '';
  phoneNumberError: boolean = false;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService,
    readonly redirectService: RedirectService,
    readonly formatHelperService: FormatHelperService,
    readonly formBuilder: UntypedFormBuilder
  ) {

  }

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.currentUser = currentUser;
        this.verifyUserForm = this.formBuilder.group({
          address1: [this.currentUser.street1, Validators.required],
          address2: this.currentUser.street2,
          city: [this.currentUser.city, Validators.required],
          dateOfBirth: [this.currentUser.dateOfBirth, Validators.required],
          firstName: [this.currentUser.firstName, Validators.required],
          lastName: [this.currentUser.lastName, Validators.required],
          mfaEnabled: [null],
          phone: [this.currentUser.phone, [Validators.required]],
          state: [this.currentUser.state, Validators.required],
          zip: [this.currentUser.zip, Validators.required],
        });
      });
  }

  onSubmit() {
    const profile = {
      city: this.verifyUserForm.value.city.trim(),
      date_of_birth: new Date(this.verifyUserForm.value.dateOfBirth).getTime() / 1000,
      first_name: this.verifyUserForm.value.firstName.trim(),
      id: this.currentUser.id,
      last_name: this.verifyUserForm.value.lastName.trim(),
      mfa_enabled: this.verifyUserForm.value.mfaEnabled,
      phone_e_164: this.verifyUserForm.value.phone.trim(),
      state: this.verifyUserForm.value.state.trim(),
      street_1: this.verifyUserForm.value.address1.trim(),
      street_2: this.verifyUserForm.value.address2 ? this.verifyUserForm.value.address2.trim() : '',
      zip: this.verifyUserForm.value.zip.trim(),
    };
    this.spinnerService.show('app-spinner');
    this.userService
      .updateUserProfile(profile)
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe((data: User) => {
        if (data) {
          this.verifyUserForm.markAsPristine();
          this.store.dispatch(setCurrentUser({ data }));
          this.redirectService.redirectToDashboard();
        }
      });
  }

  onCancel() {
    this.redirectService.redirectToDashboard();
  }

  get isFormDirty(): boolean {
    return this.verifyUserForm.dirty;
  }

  formatPhone($event: any) {
    this.phoneNumberError = this.formatHelperService.formatPhone($event, this.verifyUserForm.controls.phone)
  }
}
